import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext, useEffect, useState } from "react";
import {
  OrganizationNiche,
  OrganizationProfile,
  OrganizationVaProfile,
} from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import TemplateEditButton from "components/TemplateEditButton";
import { MAXIMUM_FOLLOWUP_ALLOWED } from "constants/templates";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import EditTemplateDialog from "features/Influencer/Templates/TemplatesTable/EditTemplate";
import { fetcherAuth } from "utils/api";
import { makeDeepCopy } from "utils/updateLocalState";

import { SaveOrgVaProfileFunc } from "../../schema";
import CreateTemplateSetUp from "./CreateTemplateSetUp";

const styles = {
  textField: {
    backgroundColor: "white",
    "& .MuiInputBase-root": { backgroundColor: "white" },
    "& .MuiFormHelperText-root": { color: "error.main" },
  },
};

interface Props {
  organizationProfile?: OrganizationProfile | null;
  organizationVaProfile: OrganizationVaProfile | null;
  saveOrgVaProfile: SaveOrgVaProfileFunc;
  handleBack: () => void;
  niche?: OrganizationNiche;
  handleNextStep: (updatedNiche?: OrganizationNiche) => void;
}
const FromScratch = ({
  organizationProfile,
  organizationVaProfile,
  saveOrgVaProfile,
  handleBack,
  niche,
  handleNextStep,
}: Props) => {
  const { getToken } = useAuth();
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const {
    outreachTemplates,
    followupTemplates,
    editTemplateDialogId,
    setEditTemplateDialogId,
  } = useContext(OutreachTemplatesContext);
  const { currentOrg } = useContext(OrganizationUserContext);

  const [defaultOutreach, setDefaultOutreach] = useState<Template | undefined>(
    undefined,
  );
  const [indexOfEditFollowUp, setIndexOfEditFollowUp] = useState<number | null>(
    null,
  );
  const [defaultFollowUps, setDefaultFollowUps] = useState<Template[]>([]);
  const [submitLoading, setSubmitLoading] = useState(false);

  const defaultTemplate = {
    subject: "",
    contentType: TemplateContentType.OUTREACH,
    body: "",
    name: "Outreach Template",
  };

  const [createTemplateOpen, setCreateTemplateOpen] =
    useState<TemplateContentType | null>(null);
  const [editTemplate, setEditTemplate] = useState<Template>(defaultTemplate);

  const handleDeleteFollowUp = (index: number) => {
    const updatedFollowUps = makeDeepCopy(defaultFollowUps);
    updatedFollowUps.splice(index, 1);
    setDefaultFollowUps(updatedFollowUps);
  };

  const onOutreachChange = (id: number) => {
    const template = outreachTemplates?.find((t) => t.id === id);
    if (id === -1) {
      setCreateTemplateOpen(TemplateContentType.OUTREACH);
      setEditTemplate({
        subject: "",
        contentType: TemplateContentType.OUTREACH,
        body: "",
        name: "Outreach Template",
      });
      return;
    }
    if (template) {
      setDefaultOutreach(template);
    } else {
      setDefaultOutreach(undefined);
    }
  };

  const onFollowUpChange = (id: number, index: number) => {
    setIndexOfEditFollowUp(index);
    if (id === -1) {
      setCreateTemplateOpen(TemplateContentType.FOLLOWUP);
      setEditTemplate({
        subject: "",
        contentType: TemplateContentType.FOLLOWUP,
        body: "",
        name: `Follow-Up Template ${index + 1}`,
      });
      return;
    }
    const template = followupTemplates?.find((t) => t.id === id);
    setDefaultFollowUps((prevFollowUps) => {
      const updatedFollowUps = [...prevFollowUps];
      if (template) {
        updatedFollowUps[index] = template;
      }
      return updatedFollowUps;
    });
  };

  const handleAddFollowUp = () => {
    if (followupTemplates?.length === 0) {
      setIndexOfEditFollowUp(0);
      setCreateTemplateOpen(TemplateContentType.FOLLOWUP);
      setEditTemplate({
        subject: "",
        contentType: TemplateContentType.FOLLOWUP,
        body: "",
        name: `Follow-Up Template ${defaultFollowUps.length + 1}`,
      });
      return;
    }
    if (defaultFollowUps.length >= MAXIMUM_FOLLOWUP_ALLOWED) {
      return;
    }
    const copy = makeDeepCopy(defaultFollowUps);
    copy.push(followupTemplates[0]);
    setIndexOfEditFollowUp(copy.length - 1);
    setDefaultFollowUps(copy);
  };

  const handleNext = () => {
    if (niche?.id) {
      handleSubmitForNiche();
    } else {
      handleSubmitDefault();
    }
  };

  const handleSubmitForNiche = async () => {
    if (!defaultOutreach || !niche?.id) {
      setAlert("Please select a default outreach template", "warning");
      return;
    }

    const allTemplateIds = [defaultOutreach?.id];
    const followUpIds = defaultFollowUps.map((followUp) => followUp.id);
    if (followUpIds) {
      allTemplateIds.push(...followUpIds);
    }
    setSubmitLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/organization-niches/${niche?.id}/templates`,
        "PUT",
        {},
        {
          templateIds: allTemplateIds,
        },
      );
      setAlert("Successfully saved templates for this niche", "success");
      const copy = makeDeepCopy(niche || {});
      copy.templates = [defaultOutreach, ...defaultFollowUps];
      handleNextStep(copy);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSubmitDefault = async () => {
    if (!defaultOutreach) {
      setAlert("Please select a default outreach template", "warning");
      return;
    }

    const copy = makeDeepCopy(organizationVaProfile || {});
    copy.fallbackTemplateIds = [defaultOutreach.id];
    const followUpIds = defaultFollowUps.map((followUp) => followUp.id);
    if (followUpIds) {
      copy.fallbackTemplateIds.push(...followUpIds);
    }
    saveOrgVaProfile(copy, false, false);
    handleNextStep();
  };

  const initializeDefaultTemplate = () => {
    if (outreachTemplates?.length > 0 && !defaultOutreach) {
      const defaultOutreachTemplate = outreachTemplates[0];
      setDefaultOutreach(defaultOutreachTemplate);
    }
    if (followupTemplates?.length > 0 && defaultFollowUps.length === 0) {
      const defaultFollowUpTemplate = followupTemplates[0];
      setDefaultFollowUps([defaultFollowUpTemplate]);
    }
  };

  useEffect(() => {
    if (niche?.id && niche?.templates) {
      const defaultOutreach = niche.templates.find(
        (t) => t.contentType === TemplateContentType.OUTREACH,
      );
      const followups = niche.templates.filter(
        (t) => t.contentType === TemplateContentType.FOLLOWUP,
      );
      if (defaultOutreach) {
        setDefaultOutreach(defaultOutreach);
      }
      if (followups?.length > 0 && defaultFollowUps?.length === 0) {
        setDefaultFollowUps(followups);
      }
    } else if (!niche || !niche?.emailSettingId) {
      initializeDefaultTemplate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [niche?.id, outreachTemplates?.length, followupTemplates?.length]);

  return (
    <>
      <Typography variant="h6" gutterBottom sx={{ textAlign: "center", mt: 0 }}>
        {niche?.name
          ? `Set up ${niche?.name} Templates`
          : "Set up Fallback Templates"}
      </Typography>
      <Typography sx={{ mb: 4 }} textAlign={"center"}>
        {niche?.name ? (
          <>
            Your templates will be used to pitch to brands in the{" "}
            <strong>{niche?.name}</strong> niche.
          </>
        ) : (
          <>
            Your templates should be generic enough to work for{" "}
            <strong>ALL niches</strong>. We use this template for any brands
            that you save to be pitched to that do not fit within one of your
            niches.
          </>
        )}
      </Typography>

      <Grid container alignItems="center" sx={{ mb: 1 }} gap={2}>
        <Grid item xs={2}>
          <strong>Outreach</strong>
        </Grid>
        <Grid item xs>
          {outreachTemplates?.length > 0 ? (
            <FormControl fullWidth>
              <Select
                name={TemplateContentType.OUTREACH}
                sx={styles.textField}
                value={defaultOutreach?.id || ""}
                onChange={(e: CustomEvent) => onOutreachChange(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">
                  <em>Select Outreach</em>
                </MenuItem>
                {outreachTemplates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))}
                <MenuItem value={-1} sx={{ color: "green.main" }}>
                  <em>Create a New Outreach </em>
                </MenuItem>
              </Select>
            </FormControl>
          ) : (
            <Button
              variant="contained"
              disableElevation
              onClick={() =>
                setCreateTemplateOpen(TemplateContentType.OUTREACH)
              }
            >
              Create Template ✨
            </Button>
          )}
        </Grid>

        <Grid item xs={1}>
          {defaultOutreach?.id && (
            <TemplateEditButton templateId={defaultOutreach?.id} />
          )}
        </Grid>
      </Grid>

      {defaultFollowUps?.length > 0 &&
        defaultFollowUps?.map((defaultFollowUp, index) => (
          <Grid container alignItems="center" gap={2} sx={{ mb: 1 }}>
            <Grid item xs={2}>
              <strong>Follow-Up {index + 1}</strong>
            </Grid>
            <Grid item xs>
              <FormControl fullWidth>
                <Select
                  displayEmpty
                  name={TemplateContentType.FOLLOWUP}
                  sx={styles.textField}
                  value={defaultFollowUp?.id || ""}
                  onChange={(e: CustomEvent) =>
                    onFollowUpChange(e.target.value, index)
                  }
                >
                  {followupTemplates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                  <MenuItem value={-1} sx={{ color: "green.main" }}>
                    <em>Create a New Follow-Up </em>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid container item xs={1}>
              {defaultFollowUp?.id && (
                <TemplateEditButton templateId={defaultFollowUp?.id} />
              )}
              {defaultFollowUp?.id && (
                <Tooltip title="Delete follow-up">
                  <IconButton onClick={() => handleDeleteFollowUp(index)}>
                    <Box
                      sx={{ fontSize: 14 }}
                      component="i"
                      className="fa-regular fa-trash"
                    />
                  </IconButton>
                </Tooltip>
              )}
            </Grid>
          </Grid>
        ))}

      {defaultFollowUps?.length < MAXIMUM_FOLLOWUP_ALLOWED && (
        <Grid container alignItems="center" gap={2}>
          <Grid item xs={2}></Grid>
          <Grid item xs>
            <Button
              sx={{ fontSize: 12, textTransform: "none" }}
              size="small"
              onClick={handleAddFollowUp}
              disabled={outreachTemplates?.length === 0}
            >
              Add Follow Up +
            </Button>
          </Grid>
        </Grid>
      )}

      {editTemplateDialogId && (
        <EditTemplateDialog
          propsTemplateId={editTemplateDialogId}
          propsHandleClose={() => {
            setEditTemplateDialogId(null);
          }}
        />
      )}

      <CreateTemplateSetUp
        open={Boolean(createTemplateOpen)}
        handleClose={() => {
          setCreateTemplateOpen(null);
          setEditTemplate(defaultTemplate);
        }}
        editTemplate={editTemplate}
        setEditTemplate={setEditTemplate}
        handleNext={(newTemplate?: Template) => {
          if (
            newTemplate &&
            newTemplate?.contentType === TemplateContentType.OUTREACH
          ) {
            setDefaultOutreach(newTemplate);
          } else if (
            newTemplate &&
            newTemplate?.contentType === TemplateContentType.FOLLOWUP
          ) {
            if (indexOfEditFollowUp !== null) {
              setDefaultFollowUps((prevFollowUps) => {
                const updatedFollowUps = [...prevFollowUps];
                if (indexOfEditFollowUp < updatedFollowUps?.length) {
                  updatedFollowUps[indexOfEditFollowUp] = newTemplate;
                } else {
                  updatedFollowUps.push(newTemplate);
                }
                return updatedFollowUps;
              });
            }
          }
          setCreateTemplateOpen(null);
        }}
        allowChangeTitle={true}
      />

      <Grid item xs="auto" container columnGap={1} sx={{ mt: 4 }}>
        {!(outreachTemplates?.length > 0) && (
          <Grid item xs="auto">
            <Button disableElevation onClick={handleBack}>
              <Box
                component="i"
                className="fa-solid fa-arrow-left"
                sx={{ mr: 1 }}
              />
              Back
            </Button>
          </Grid>
        )}
        <Grid item xs></Grid>
        <Grid item xs="auto">
          <LoadingButton
            loading={submitLoading}
            disableElevation
            onClick={handleNext}
            variant="contained"
          >
            Save
          </LoadingButton>
        </Grid>
      </Grid>
    </>
  );
};

export default FromScratch;
